import React, { useState } from "react";
import PropTypes from "prop-types";
import { Badge, Button, ButtonGroup, Form, Input } from "reactstrap";
import { deviceShape, storeShape } from "../../shared/propShapes";
import { isTemp } from "../treeReducer";
import FieldRow, { Section } from "./FieldRow";
import isPrivateIp from "../../../../utils/ipUtils";

function toNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

export default function CaptureDeviceForm({ device, brands, store, dispatch, canCreate }) {
  const [revealPassword, setRevealPassword] = useState(false);
  const isCloud = !!store && store.local_inference === false;
  // Cloud-inference stores are reached over the public internet, so a private
  // host can never work (mirrors backend PLA-2045). The hard block is the API;
  // this is inline UX feedback.
  const hostIsPrivate = isCloud && isPrivateIp(device.host);

  const update = (patch) => dispatch({ type: "UPDATE_DEVICE", publicId: device.public_id, patch });
  const set = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    update({ [field]: value });
  };
  const setNum = (field) => (e) => update({ [field]: toNumber(e.target.value) });

  const onRevert = () => dispatch({ type: "REVERT_ENTITY", publicId: device.public_id });
  const onDelete = () => dispatch({ type: "DELETE_DEVICE", publicId: device.public_id });
  const onDuplicate = () => dispatch({ type: "DUPLICATE_DEVICE", publicId: device.public_id });
  const onAddCamera = () => dispatch({ type: "ADD_CAMERA", deviceId: device.public_id, partial: {} });

  // Mirrors legacy VideoRecorderForm: cloud stores expose the router-facing ports
  // (the primary pair) plus the recorder-facing ports (the redirected pair).
  // Local stores expose only the recorder-facing ports.
  const primaryRtspLabel = isCloud ? "Router RTSP port" : "Video Recorder RTSP port";
  const primaryHttpLabel = isCloud ? "Router HTTP port" : "Video Recorder HTTP port";
  const hostHint = isCloud
    ? "Public IP or DDNS hostname — private IPs (192.168.x, 10.x, 172.16–31.x) are rejected. E.g. xxx.link-dvr.com"
    : "IP or hostname. E.g. 192.168.1.10 | 192.168.1.{} | dvr.local";

  return (
    <Form>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="mb-0">
            🖥️ Capture Device
            {isTemp(device.public_id) && (
              <Badge color="info" className="ml-2" style={{ fontSize: "0.65rem" }}>
                new
              </Badge>
            )}
            {device.localDeleted && (
              <Badge color="danger" className="ml-2" style={{ fontSize: "0.65rem" }}>
                marked for deletion
              </Badge>
            )}
          </h6>
          <code style={{ fontSize: "0.75rem", color: "#6c757d" }}>{device.public_id}</code>
        </div>
        <Button size="sm" outline color="secondary" onClick={onRevert}>
          ↺ Revert
        </Button>
      </div>

      <Section title="Identity">
        <FieldRow label="Name">
          <Input bsSize="sm" value={device.name || ""} onChange={set("name")} />
        </FieldRow>
        <FieldRow label="Brand">
          <Input
            type="select"
            bsSize="sm"
            className="custom-select"
            value={device.brand?.id ?? device.brand ?? ""}
            onChange={(e) => update({ brand: e.target.value === "" ? null : toNumber(e.target.value) })}
          >
            <option value="">— select brand —</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} ({b.kind})
              </option>
            ))}
          </Input>
        </FieldRow>
        <FieldRow label="Transport">
          <Input
            type="select"
            bsSize="sm"
            className="custom-select"
            value={device.transport}
            onChange={set("transport")}
          >
            <option>RTSP</option>
            <option>RTSPS</option>
            <option>HTTP</option>
          </Input>
        </FieldRow>
      </Section>

      <Section title="Network">
        <FieldRow label="Host" hint={hostHint}>
          <Input
            bsSize="sm"
            value={device.host || ""}
            onChange={(e) => update({ host: e.target.value.trim() })}
            invalid={hostIsPrivate}
          />
          {hostIsPrivate && (
            <div className="text-danger" style={{ fontSize: "0.75rem", marginTop: 2 }}>
              Private IP not allowed on a cloud-inference store — use a publicly reachable IP or hostname.
            </div>
          )}
        </FieldRow>
        <FieldRow label={primaryRtspLabel}>
          <Input type="number" bsSize="sm" value={device.rtsp_port ?? ""} onChange={setNum("rtsp_port")} />
        </FieldRow>
        <FieldRow label={primaryHttpLabel} hint="Leave empty for N/A">
          <Input type="number" bsSize="sm" value={device.api_port ?? ""} onChange={setNum("api_port")} />
        </FieldRow>
        {isCloud && (
          <>
            <FieldRow label="Video Recorder RTSP port">
              <Input
                type="number"
                bsSize="sm"
                value={device.rtsp_port_redirected ?? ""}
                onChange={setNum("rtsp_port_redirected")}
              />
            </FieldRow>
            <FieldRow label="Video Recorder HTTP port" hint="Leave empty for N/A">
              <Input
                type="number"
                bsSize="sm"
                value={device.api_port_redirected ?? ""}
                onChange={setNum("api_port_redirected")}
              />
            </FieldRow>
          </>
        )}
      </Section>

      <Section title="Credentials">
        <FieldRow label="Username">
          <Input bsSize="sm" value={device.username || ""} onChange={set("username")} />
        </FieldRow>
        <FieldRow label="Password" hint="Leave empty to keep current; type to overwrite">
          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <Input
              bsSize="sm"
              type={revealPassword ? "text" : "password"}
              value={device.password || ""}
              onChange={set("password")}
              placeholder="(unchanged)"
            />
            <Button
              size="sm"
              outline
              type="button"
              onClick={() => setRevealPassword((v) => !v)}
              style={{ whiteSpace: "nowrap" }}
            >
              {revealPassword ? "🙈 hide" : "👁 show"}
            </Button>
          </div>
        </FieldRow>
      </Section>

      <div className="d-flex justify-content-between mt-3">
        {canCreate && (
          <ButtonGroup size="sm">
            <Button color="success" onClick={onAddCamera} disabled={device.localDeleted}>
              + Add camera
            </Button>
            <Button outline color="secondary" onClick={onDuplicate} disabled={device.localDeleted}>
              Duplicate device
            </Button>
          </ButtonGroup>
        )}
        <Button size="sm" outline color="danger" onClick={onDelete} disabled={device.localDeleted}>
          {device.localDeleted ? "Marked for deletion" : "Delete device"}
        </Button>
      </div>
    </Form>
  );
}

CaptureDeviceForm.propTypes = {
  device: deviceShape.isRequired,
  brands: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })).isRequired,
  store: storeShape,
  dispatch: PropTypes.func.isRequired,
  /** Whether the user may create entities — the `editorRole` gate carried by the legacy recorders. */
  canCreate: PropTypes.bool.isRequired,
};

CaptureDeviceForm.defaultProps = { store: null };
